import React from 'react';
import styled from 'styled-components';
import appTheme from '../../common/theme/appTheme.json';
import { ThemeInterface } from '../../common/theme/AppThemeInterface';
import { minutesToFormattedMinutesStringColonDelimitedFormat } from '../../../utils/DateTimeUtils';
import { HNA_ALL_ALTITUDE_RESTRICTION_ALTITUDE_FT } from '../../../utils/MptFlightPathSegmentingUtils';

const theme: ThemeInterface = appTheme;

/*
Warning do not use lodash in this componet  or it crashes in IE cause of polyfills
 */

export interface HandoffMarkerDialogContentProps {
  title: string;
  enter?: string;
  exit?: string;
  beam?: string;
  isLandingOrTakeoff?: boolean;
  timeSinceTakeOffMin: number;
  userSetTakeoffTimeMin: number;
  hnaAltitude?: number;
  serviceAlertEventType: ServiceAlertEventType;
}

export enum ServiceAlertEventType {
  HAND_OFF = 'HAND_OFF',
  ENTER_COVERAGE = 'ENTER_COVERAGE',
  ENTER_NO_COVERAGE = 'ENTER_NO_COVERAGE',
  ENTER_ALL_ALTITUDE_HNA = 'ENTER_ALL_ALTITUDE_HNA',
  ENTER_ALTITUDE_RESTRICTED_HNA = 'ENTER_ALTITUDE_RESTRICTED_HNA',
}

class MarkerDialogContent extends React.PureComponent<HandoffMarkerDialogContentProps> {
  render() {
    const {
      title,
      exit,
      enter,
      beam,
      timeSinceTakeOffMin,
      userSetTakeoffTimeMin,
      serviceAlertEventType,
      hnaAltitude,
      isLandingOrTakeoff,
    } = this.props;
    const textColorClass = getTextColorClassForServiceAlertPanel(serviceAlertEventType);
    const backgroundColorClass = getBackgroundClassColorForServiceAlertPanel(serviceAlertEventType);
    return (
      <StyledPanel className="markerDialogPanel" serviceAlertEventType={serviceAlertEventType}>
        <div className={`header ${backgroundColorClass} ${textColorClass}`}>{title}</div>
        {hnaAltitude !== undefined &&
          hnaAltitude !== null &&
          (serviceAlertEventType === ServiceAlertEventType.ENTER_ALL_ALTITUDE_HNA ||
            serviceAlertEventType === ServiceAlertEventType.ENTER_ALTITUDE_RESTRICTED_HNA) && (
            <div className={`sub__heading ${backgroundColorClass} ${textColorClass}`}>
              {getAltitudeString(hnaAltitude)}
            </div>
          )}
        {isLandingOrTakeoff ? (
          <div className="beam__row">
            <div className="beam__label">BEAM:</div>
            <div className="beam__name">{beam}</div>
          </div>
        ) : (
          <React.Fragment>
            <div className="beam__row">
              <div className="beam__label">EXIT:</div>
              <div className="beam__name">{exit}</div>
            </div>

            <div className="beam__row">
              <div className="beam__label">ENTER:</div>
              <div className="beam__name">{enter}</div>
            </div>
          </React.Fragment>
        )}

        <div className="time">
          {`T+${minutesToFormattedMinutesStringColonDelimitedFormat(timeSinceTakeOffMin)}`}
          {userSetTakeoffTimeMin && userSetTakeoffTimeMin > -1 // userSetTakeoffTimeMin is -1 when user hasn't set a takeoff time
            ? ` (${minutesToFormattedMinutesStringColonDelimitedFormat(userSetTakeoffTimeMin)}Z) `
            : ''}
        </div>
      </StyledPanel>
    );
  }
}

export default MarkerDialogContent;

const getBackgroundClassColorForServiceAlertPanel = (serviceAlertEventType: ServiceAlertEventType): string => {
  let color: string = 'white';
  switch (serviceAlertEventType) {
    case ServiceAlertEventType.ENTER_COVERAGE:
      color = 'background__color-nominal';
      break;
    case ServiceAlertEventType.ENTER_ALL_ALTITUDE_HNA:
      color = 'background__color-hna_all_altitude';
      break;
    case ServiceAlertEventType.ENTER_ALTITUDE_RESTRICTED_HNA:
      color = 'background__color-hna_restricted';
      break;
    case ServiceAlertEventType.ENTER_NO_COVERAGE:
      color = 'background__color-no_coverage';
      break;
    case ServiceAlertEventType.HAND_OFF:
      color = 'background__color-hand_off';
      break;
    default:
      break;
  }
  return color;
};
const getTextColorClassForServiceAlertPanel = (serviceAlertEventType: ServiceAlertEventType): string => {
  let color: string = 'white';
  switch (serviceAlertEventType) {
    case ServiceAlertEventType.ENTER_COVERAGE:
    case ServiceAlertEventType.ENTER_ALTITUDE_RESTRICTED_HNA:
    case ServiceAlertEventType.ENTER_NO_COVERAGE:
    case ServiceAlertEventType.HAND_OFF:
      color = 'text__color-dark';
      break;
    case ServiceAlertEventType.ENTER_ALL_ALTITUDE_HNA:
      color = 'text__color-light';
      break;
    default:
      break;
  }
  return color;
};

export const getAltitudeString = (hnaAltitude: number | undefined) => {
  let hnaAltitudeToDisplay = '';
  if (hnaAltitude === HNA_ALL_ALTITUDE_RESTRICTION_ALTITUDE_FT) {
    hnaAltitudeToDisplay = 'ALTITUDE RESTRICTION: ALL AIRSPACE';
  } else if (hnaAltitude !== undefined && hnaAltitude !== null) {
    hnaAltitudeToDisplay = `ALTITUDE RESTRICTION: ${hnaAltitude.toLocaleString()} ft`;
  }
  return hnaAltitudeToDisplay;
};

const StyledPanel = styled.div`
  box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.44);

  min-height: 85px;
  min-width: 200px;
  background-color: white;
  .background__color-nominal {
    background-color:${theme.mptNominalServiceFlightPathColor} !important;
  }
  .background__color-hna_all_altitude {  
    background-color:${theme.mptHnaAllAltitudeFlightPathColor} !important;
  } 
  .background__color-hna_restricted {  
    background-color:${theme.mptHnaAltitudeRestrictedFlightPathColor} !important;
  }
  .background__color-no_coverage {  
    background-color:${theme.mptNoCoverageFlightPathColor} !important;
  }
  .background__color-hand_off {  
    background-color:${theme.mptDegradedServiceFlightPathColor} !important;
  }
  .text__color-light {  
    color:${theme.serviceAlertDialogLightTextColor} !important;
  }
  .text__color-dark {  
    color:${theme.serviceAlertDialogDarkTextColor} !important;
  }
  
  .close__button {
    position: absolute !important;
    top: 0px;

    right: 0px;
    height: 32px;
    width: 32px;
    padding: 0px;
    .close__icon {
      font-size: 16px;
      opacity: 0
    }
  }
  .header {
    height: 32px;
    font-family: ${theme.appFontFamily};
    letter-spacing: -0.08px;
    font-size: 14px;
    line-height: 32px;
    padding-left: 12px;
    padding-right: 32px;
    font-weight: bold;
    margin-bottom: 12px;
  }
  .sub__heading {
    padding: 6px 12px;
    margin-top: -24px;
    font-family: ${theme.appFontFamily};
    letter-spacing: -0.08px;
    font-size: 12px;
    margin-bottom: 12px;
  }
    .beam__row {
      font-size: 12px;
      letter-spacing: -0.08px;
      min-height: 18px;
      vertical-align: bottom;
      display: flex;
      padding-left: 56px;
    }
    .beam__label {
      display: inline-block;
      font-size: 12px;
      letter-spacing: -0.08px;
      box-sizing: border-box;
      font-weight: bold;
      min-height: 18px;
      line-height: 20px;
      font-family: ${theme.appFontFamily};
      left: 12px;
      position: absolute;
    }
  }
  .beam__name {
    display: inline-block;
    font-size: 14px;
    letter-spacing: -0.08px;
    font-weight: normal;
    opacity: 0.8;
    min-height: 18px;
    line-height: 18px;
    font-family: ${theme.appFontFamily};
    box-sizing: border-box;
    position: relative;
    padding-right: 12px;
  }
  .time {
    display: block;
    width: 100%;
    text-align: right;
    font-size: 14px;
    letter-spacing: -0.08px;
    font-family: ${theme.appFontFamily};
    font-weight: bold;
    line-height: 18px;
    box-sizing: border-box;
    padding: 6px 12px;
  }
`;
